import {
  mountFilterMenu,
  type FilterMenuHandle,
  type FilterMenuOption,
} from "./filter-menu";

export interface SelectMenuEntry {
  value: string;
  label: string;
  group?: string | null;
}

export interface SelectMenuHandle {
  sync(): boolean;
  focus(): void;
  destroy(): void;
}

export function selectMenuOptionsFromEntries(
  entries: readonly SelectMenuEntry[],
): FilterMenuOption[] {
  const seen = new Set<string>();
  return entries.map((entry) => {
    if (seen.has(entry.value)) {
      throw new Error(`A select menu cannot repeat value "${entry.value}".`);
    }
    seen.add(entry.value);
    const label = entry.label.trim();
    if (!label) {
      throw new Error("A select menu option needs a visible label.");
    }
    const group = entry.group?.trim();
    return {
      value: entry.value,
      label,
      ...(group ? { group } : {}),
    };
  });
}

function entriesFromSelect(select: HTMLSelectElement): SelectMenuEntry[] {
  return [...select.options].map((option) => ({
    value: option.value,
    label: option.label,
    group: option.parentElement instanceof HTMLOptGroupElement
      ? option.parentElement.label
      : null,
  }));
}

function restoreAttribute(
  element: HTMLElement,
  name: string,
  value: string | null,
): void {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
}

export function mountSelectMenu(
  select: HTMLSelectElement,
): SelectMenuHandle {
  if (!select.id) {
    throw new Error("A select menu needs a stable backing select ID.");
  }
  const labels = [...select.labels];
  const label = labels[0];
  if (!label) {
    throw new Error(`Select menu "${select.id}" needs an associated label.`);
  }

  const originalLabelID = label.getAttribute("id");
  const originalLabelFors = labels.map((associatedLabel) => (
    associatedLabel.getAttribute("for")
  ));
  const originalAriaHidden = select.getAttribute("aria-hidden");
  const originalTabIndex = select.getAttribute("tabindex");
  if (!label.id) label.id = `${select.id}-label`;

  const root = document.createElement("div");
  select.insertAdjacentElement("afterend", root);
  const filter: FilterMenuHandle = mountFilterMenu(root, {
    id: `${select.id}-control`,
    labelID: label.id,
    options: selectMenuOptionsFromEntries(entriesFromSelect(select)),
    value: select.value,
    describedBy: select.getAttribute("aria-describedby") ?? undefined,
    disabled: select.disabled,
    invalid: select.getAttribute("aria-invalid") === "true",
    onChange(value) {
      select.value = value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    },
  });
  root.classList.add("select-menu");
  select.classList.add("select-menu-native");
  select.setAttribute("aria-hidden", "true");
  select.tabIndex = -1;
  labels.forEach((associatedLabel) => {
    associatedLabel.htmlFor = `${select.id}-control`;
  });

  const abort = new AbortController();
  const { signal } = abort;
  const sync = (): boolean => {
    filter.setDescribedBy(
      select.getAttribute("aria-describedby") ?? undefined,
    );
    filter.setDisabled(select.disabled);
    filter.setInvalid(select.getAttribute("aria-invalid") === "true");
    return filter.setValue(select.value);
  };
  const focus = () => filter.focus();

  select.addEventListener("change", sync, { signal });
  select.addEventListener("focus", focus, { signal });

  return {
    sync,
    focus,
    destroy(): void {
      abort.abort();
      filter.destroy();
      root.remove();
      select.classList.remove("select-menu-native");
      restoreAttribute(select, "aria-hidden", originalAriaHidden);
      restoreAttribute(select, "tabindex", originalTabIndex);
      restoreAttribute(label, "id", originalLabelID);
      labels.forEach((associatedLabel, index) => {
        restoreAttribute(
          associatedLabel,
          "for",
          originalLabelFors[index] ?? null,
        );
      });
    },
  };
}
